/**
 * Nucery cart PAGE (/cart) — same cart logic as the drawer, applied to a full
 * page. Uses /cart/change.js (qty/remove) + /cart/update.js (discount) and
 * re-renders in place. All hooks use data-ncp-* so the site-wide drawer JS
 * never intercepts page controls.
 */
(function () {
  'use strict';

  /**
   * Locale/market-aware cart URL. Shopify Markets prefixes storefront routes
   * (e.g. /en-sa/cart.js), so a bare /cart.js 404s on localized URLs. Use the
   * route root Shopify exposes — it is '/' on the primary locale.
   */
  function cartUrl(path) {
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return base + path;
  }

  var root;
  var config = {};

  function qs(sel, ctx) {
    return (ctx || root).querySelector(sel);
  }
  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || root).querySelectorAll(sel));
  }

  function ensureRoot() {
    root = document.getElementById('NuceryCartPage');
    return root;
  }

  function readConfig() {
    var el = root && root.querySelector('script[type="application/json"][data-ncp-config]');
    try {
      config = el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      config = {};
    }
    config.strings = config.strings || {};
    config.shopCurrency = config.shopCurrency || config.currency || 'USD';
    applyThresholdsFromConfig();
  }

  /* ---------- money ---------- */
  // Active display currency + base→active rate (storefront multi-currency).
  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency || config.shopCurrency || 'USD';
  }
  function activeRate() {
    var r = (window.Shopify && window.Shopify.currency) ? parseFloat(window.Shopify.currency.rate) : NaN;
    return (isFinite(r) && r > 0) ? r : 1;
  }
  function formatMoneyRaw(cents) {
    cents = Number(cents) || 0;
    var shopCur = (config.shopCurrency || config.currency || 'USD').toString();
    var active = String(activeCurrency());
    var fmt = config.moneyFormat || (window.theme && window.theme.settings && window.theme.settings.moneyFormat);
    var shopifyOk = window.Shopify && typeof Shopify.formatMoney === 'function' && fmt;
    // Viewing the base currency → use the theme money format as-is.
    if (active.toUpperCase() === shopCur.toUpperCase()) {
      if (shopifyOk) return Shopify.formatMoney(cents, fmt);
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + active;
      }
    }
    // Different display currency → convert base→active by the storefront rate.
    var converted = cents * activeRate();
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: active }).format(converted / 100);
    } catch (e) {
      return (converted / 100).toFixed(2) + ' ' + active;
    }
  }

  function stripHtml(html) {
    var d = document.createElement('div');
    d.innerHTML = String(html == null ? '' : html);
    return (d.textContent || d.innerText || '').trim();
  }

  // Plain text (no .money span) so the Vitals currency converter can't attach a
  // base-currency tooltip to any cart price.
  function formatMoney(cents) {
    return stripHtml(formatMoneyRaw(cents));
  }

  /* ---------- thresholds / progress (mirrors drawer) ---------- */
  function parseAmountToCents(raw) {
    if (raw == null) return null;
    var s = String(raw).trim().replace(/\s/g, '');
    if (s === '') return null;
    s = s.replace(',', '.').replace(/[^\d.-]/g, '');
    var n = parseFloat(s);
    if (!isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  }

  function convertShopMinorToCartMinor(shopMinor) {
    var n = Number(shopMinor);
    if (!isFinite(n)) return 0;
    var shop = (config.shopCurrency || '').toString().toUpperCase();
    var cartCur = (config.currency || '').toString().toUpperCase();
    if (!shop || !cartCur || shop === cartCur) return Math.round(n);
    var sc = window.Shopify && window.Shopify.currency;
    var rate = sc && sc.rate != null ? Number(sc.rate) : NaN;
    if (!isFinite(rate) || rate <= 0) return Math.round(n);
    return Math.round(n * rate);
  }

  function applyThresholdsFromConfig() {
    var s1 = parseAmountToCents(config.shippingAmountRaw);
    var s2 = parseAmountToCents(config.giftAmountRaw);
    var fb1 = Number(config.shippingThresholdCentsFallback);
    var fb2 = Number(config.giftThresholdCentsFallback);
    if (!isFinite(fb1)) fb1 = 3500;
    if (!isFinite(fb2)) fb2 = 7000;
    config.shippingThresholdCents = convertShopMinorToCartMinor(s1 != null ? s1 : fb1);
    config.giftThresholdCents = convertShopMinorToCartMinor(s2 != null ? s2 : fb2);
    if (config.giftThresholdCents <= config.shippingThresholdCents) {
      config.giftThresholdCents = config.shippingThresholdCents + 1;
    }
  }

  var SHIP_VISUAL_PCT = 50;

  function cartTotalCents(cart) {
    if (!cart) return 0;
    // Trust Shopify's post-discount total — INCLUDING 0 (e.g. a 100%-off code).
    // Only fall back to the subtotal when total_price is genuinely missing (NaN).
    var tp = Number(cart.total_price);
    if (isFinite(tp)) return tp;
    var sub = Number(cart.items_subtotal_price);
    if (isFinite(sub) && sub > 0) return sub;
    return 0;
  }

  function progressState(t) {
    var m1 = Math.max(0, Number(config.shippingThresholdCents) || 3500);
    var m2 = Math.max(0, Number(config.giftThresholdCents) || 7000);
    if (m2 <= m1) m2 = m1 + 1;
    t = t || 0;
    var shipDone = m1 === 0 ? true : t >= m1;
    var giftDone = t >= m2;
    var fillPct;
    if (t >= m2) fillPct = 100;
    else if (m1 <= 0) fillPct = (t / m2) * 100;
    else if (t < m1) fillPct = (t / m1) * SHIP_VISUAL_PCT;
    else fillPct = SHIP_VISUAL_PCT + ((t - m1) / (m2 - m1)) * (100 - SHIP_VISUAL_PCT);
    return {
      m1: m1,
      m2: m2,
      remainingShip: Math.max(0, m1 - t),
      remainingGift: Math.max(0, m2 - t),
      shipDone: shipDone,
      giftDone: giftDone,
      fillPct: Math.min(100, Math.max(0, fillPct)),
    };
  }

  function isProgressEnabled() {
    return root && root.getAttribute('data-show-progress') !== 'false';
  }

  // Keep a milestone's label + icon in sync with the section settings. The
  // server already renders these, but re-applying from config means a live
  // theme-editor change shows immediately (the section JS re-reads config on
  // shopify:section:load) instead of needing a full save/reload.
  function setMilestoneMeta(mod, label, imgUrl) {
    var anchor = qs('.nucery-cart-page__milestone-anchor--' + mod);
    if (!anchor) return;
    var nameEl = anchor.querySelector('.nucery-cart-page__milestone-name');
    if (nameEl && label != null && String(label) !== '') nameEl.textContent = String(label);
    if (!imgUrl) return;
    var ic = anchor.querySelector('.nucery-cart-page__milestone-ic');
    if (!ic) return;
    var img = ic.querySelector('img');
    if (img) {
      if (img.getAttribute('src') !== imgUrl) img.setAttribute('src', imgUrl);
    } else {
      ic.innerHTML =
        '<img class="nucery-cart-page__milestone-img" src="' + escapeAttr(imgUrl) +
        '" alt="" loading="lazy" width="48" height="48">';
    }
  }

  function renderProgress(cart) {
    if (!isProgressEnabled()) return;
    var wrap = qs('[data-ncp-progress]');
    if (!wrap) return;
    var st = progressState(cartTotalCents(cart));
    var msgEl = qs('[data-ncp-progress-msg]');
    var fill = qs('[data-ncp-progress-fill]');
    if (msgEl) {
      if (st.giftDone) msgEl.textContent = config.strings.progressAllMet || '';
      else if (st.shipDone)
        msgEl.textContent = (config.strings.progressGiftRemaining || '').replace(/__AMOUNT__/g, formatMoney(st.remainingGift));
      else
        msgEl.textContent = (config.strings.progressShippingRemaining || '').replace(/__AMOUNT__/g, formatMoney(st.remainingShip));
    }
    if (fill) fill.style.width = st.fillPct + '%';
    root.classList.toggle('ncp--ship-reached', st.shipDone);
    root.classList.toggle('ncp--gift-reached', st.giftDone);
    // Label + icon come from section settings; re-apply on every render so a
    // theme-editor edit (or an AJAX cart sync) always reflects the latest value.
    setMilestoneMeta('ship', config.milestone1Label, config.milestone1Image);
    setMilestoneMeta('gift', config.milestone2Label, config.milestone2Image);
    var m1Amt = qs('[data-ncp-milestone-ship-amt]');
    var m2Amt = qs('[data-ncp-milestone-gift-amt]');
    if (m1Amt) m1Amt.textContent = formatMoney(st.m1);
    if (m2Amt) m2Amt.textContent = formatMoney(st.m2);
    var startEl = qs('[data-ncp-track-start]');
    if (startEl) {
      var ov = config.trackStartOverride && String(config.trackStartOverride).trim();
      startEl.textContent = ov || formatMoney(0);
    }
  }

  /* ---------- fetch + line changes ---------- */
  function fetchCart() {
    return fetch(cartUrl('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function updateHeaderBadges(count) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-nucery-cart-count]'), function (el) {
      el.textContent = String(count);
      el.hidden = count < 1;
      el.setAttribute('aria-hidden', count < 1 ? 'true' : 'false');
    });
  }

  function findRow(key) {
    if (!root || !key) return null;
    return root.querySelector('.nucery-cart-page__item[data-line-key="' + (window.CSS && CSS.escape ? CSS.escape(String(key)) : String(key)) + '"]');
  }

  function changeLine(key, quantity) {
    if (!ensureRoot()) return Promise.resolve();
    var row = findRow(key);
    if (row) row.classList.add('nucery-cart-page__item--busy');
    return fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (cart && Array.isArray(cart.items)) applyCart(cart);
        return cart;
      })
      .finally(function () {
        if (row) row.classList.remove('nucery-cart-page__item--busy');
      });
  }

  var lineTimers = {};
  function queueLineChange(key, quantity) {
    if (lineTimers[key]) clearTimeout(lineTimers[key]);
    lineTimers[key] = setTimeout(function () {
      delete lineTimers[key];
      changeLine(key, quantity).catch(function () {});
    }, 300);
  }

  /* ---------- render items ---------- */
  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escapeAttr(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  var TRASH =
    '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2"></path><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6"></path><path d="M10 11v6M14 11v6"></path></svg>';

  function removeIcon() {
    if (config.removeIconStyle === 'x') return '<span class="nucery-cart-page__item-remove-x" aria-hidden="true">×</span>';
    return TRASH;
  }

  function itemImageUrl(item) {
    var u = item.image || (item.featured_image && item.featured_image.url) || '';
    if (u && u.indexOf('width=') === -1) u += (u.indexOf('?') === -1 ? '?' : '&') + 'width=240';
    return u;
  }

  // Build a readable variant label as "Name: Value / Name: Value" (bold name),
  // matching the cart drawer. Returns an HTML-escaped string ('' when there's
  // no real variant). Prefers options_with_values; falls back to variant_title.
  function variantLabel(item) {
    if (!item) return '';
    var parts = [];
    var owv = item.options_with_values;
    if (owv && owv.length) {
      for (var i = 0; i < owv.length; i++) {
        var name = owv[i] && owv[i].name ? String(owv[i].name) : '';
        var val = owv[i] && owv[i].value ? String(owv[i].value) : '';
        if (!val) continue;
        if (name === 'Title' && (val === 'Default Title' || val === 'Default')) continue;
        if (val === 'Default Title') continue;
        parts.push(
          name
            ? '<span class="nucery-cart-page__item-variant-name">' + escapeHtml(name) + ':</span> ' + escapeHtml(val)
            : escapeHtml(val)
        );
      }
    }
    if (!parts.length) {
      var vt = item.variant_title || '';
      if (vt && vt !== 'Default Title') parts.push(escapeHtml(vt));
    }
    if (!parts.length) return '';
    return parts.join(' <span class="nucery-cart-page__item-variant-sep">/</span> ');
  }

  function createItem(item) {
    var article = document.createElement('article');
    article.className = 'nucery-cart-page__item';
    article.setAttribute('data-line-key', item.key);
    article.setAttribute('data-unit-price', item.final_price);
    var img = itemImageUrl(item);
    var variant = variantLabel(item);
    article.innerHTML =
      '<a class="nucery-cart-page__item-media" href="' + escapeAttr(item.url) + '">' +
      (img
        ? '<img src="' + escapeAttr(img) + '" width="110" height="110" loading="lazy" alt="' + escapeAttr(item.product_title) + '">'
        : '<span class="nucery-cart-page__item-ph"></span>') +
      '</a>' +
      '<div class="nucery-cart-page__item-body">' +
      '<div class="nucery-cart-page__item-top"><div>' +
      '<a class="nucery-cart-page__item-title" href="' + escapeAttr(item.url) + '">' + escapeHtml(item.product_title) + '</a>' +
      (variant ? '<span class="nucery-cart-page__item-variant">' + variant + '</span>' : '') +
      '</div>' +
      '<button type="button" class="nucery-cart-page__item-remove" data-ncp-remove-line="' + escapeAttr(item.key) +
      '" aria-label="' + escapeAttr(config.strings.remove || 'Remove') + '" title="' + escapeAttr(config.strings.remove || 'Remove') + '">' +
      removeIcon() + '</button></div>' +
      '<div class="nucery-cart-page__item-bottom">' +
      '<div class="nucery-cart-page__qty" data-line-key="' + escapeAttr(item.key) + '">' +
      '<button type="button" class="nucery-cart-page__qty-btn" data-ncp-qty-delta="-1" aria-label="Decrease">−</button>' +
      '<span class="nucery-cart-page__qty-val">' + item.quantity + '</span>' +
      '<button type="button" class="nucery-cart-page__qty-btn" data-ncp-qty-delta="1" aria-label="Increase">+</button></div>' +
      '<span class="nucery-cart-page__item-price">' + formatMoney(item.final_line_price) + '</span>' +
      '</div></div>';
    return article;
  }

  function updateItem(el, item) {
    el.setAttribute('data-unit-price', item.final_price);
    var qv = el.querySelector('.nucery-cart-page__qty-val');
    if (qv) qv.textContent = String(item.quantity);
    var p = el.querySelector('.nucery-cart-page__item-price');
    if (p) p.textContent = formatMoney(item.final_line_price);
  }

  // Render one bundle as a nested block (anchor on top, pieces beneath as
  // "+ … FREE", locked "N BUNDLE" pill, single remove for the whole group).
  function createBundleGroup(name, items) {
    var anchor = null, pieces = [];
    items.forEach(function (it) {
      var role = (it.properties && (it.properties._bundle_role || it.properties['_bundle_role'])) || '';
      if (role === 'anchor' && !anchor) anchor = it; else pieces.push(it);
    });
    if (!anchor) {
      var best = null;
      items.forEach(function (it) { if (!best || Number(it.final_line_price) > Number(best.final_line_price)) best = it; });
      anchor = best || items[0];
      pieces = items.filter(function (it) { return it !== anchor; });
    }
    var keys = items.map(function (it) { return it.key; });
    var article = document.createElement('article');
    article.className = 'nucery-cart-page__item nucery-cart-page__bundle';
    article.setAttribute('data-bundle', name);
    article.setAttribute('data-bundle-keys', keys.join(','));
    var img = itemImageUrl(anchor);
    if (!img) { for (var pi = 0; pi < pieces.length; pi++) { var pu = itemImageUrl(pieces[pi]); if (pu) { img = pu; break; } } }
    var bundleQty = Number(anchor.quantity) || 1;
    var freeWord = (config.strings && config.strings.bundleFree) || 'FREE';
    var bundleWord = (config.strings && config.strings.bundleWord) || 'BUNDLE';
    var childrenHtml = pieces.map(function (it) {
      var vt = (it.variant_title && it.variant_title !== 'Default Title') ? it.variant_title : '';
      var label = escapeHtml(it.product_title || '') + (vt ? ' <span class="ncp-bc-variant">– ' + escapeHtml(vt) + '</span>' : '');
      var q = Number(it.quantity) || 1;
      var qtyTag = q > 1 ? ' <span class="ncp-bc-qty">×' + q + '</span>' : '';
      var price = Number(it.final_line_price) > 0 ? formatMoney(it.final_line_price) : freeWord;
      var cimg = itemImageUrl(it);
      var imgTag = cimg
        ? '<img class="ncp-bc-img" src="' + escapeAttr(cimg) + '" width="40" height="40" loading="lazy" alt="">'
        : '<span class="ncp-bc-img ncp-bc-img--ph"></span>';
      return '<li class="ncp-bc-row">' + imgTag +
        '<span class="ncp-bc-title">' + label + qtyTag + '</span>' +
        '<span class="ncp-bc-free">' + escapeHtml(price) + '</span></li>';
    }).join('');
    article.innerHTML =
      '<a class="nucery-cart-page__item-media" href="' + escapeAttr(anchor.url || '') + '">' +
      (img
        ? '<img src="' + escapeAttr(img) + '" width="110" height="110" loading="lazy" alt="' + escapeAttr(anchor.product_title) + '">'
        : '<span class="nucery-cart-page__item-ph"></span>') +
      '</a>' +
      '<div class="nucery-cart-page__item-body">' +
      '<div class="nucery-cart-page__item-top"><div>' +
      '<span class="nucery-cart-page__item-title">' + escapeHtml(anchor.product_title) + '</span>' +
      '</div>' +
      '<button type="button" class="nucery-cart-page__item-remove" data-ncp-remove-bundle="' + escapeAttr(name) +
      '" aria-label="' + escapeAttr(config.strings.remove || 'Remove') + '" title="' + escapeAttr(config.strings.remove || 'Remove') + '">' +
      removeIcon() + '</button></div>' +
      (childrenHtml ? '<ul class="nucery-cart-page__bundle-children">' + childrenHtml + '</ul>' : '') +
      '<div class="nucery-cart-page__item-bottom">' +
      '<div class="nucery-cart-page__qty nucery-cart-page__qty--locked nucery-cart-page__bundle-pill">' +
      '<span class="nucery-cart-page__qty-val">' + bundleQty + '</span>' +
      '<span class="nucery-cart-page__bundle-pill-lbl">' + escapeHtml(bundleWord) + '</span></div>' +
      '<span class="nucery-cart-page__item-price">' + formatMoney(anchor.final_line_price) + '</span>' +
      '</div></div>';
    return article;
  }

  // Remove a whole bundle group in one /cart/update.js call.
  function removeBundle(name) {
    var updates = {};
    qsa('[data-bundle-keys]').forEach(function (g) {
      if (g.getAttribute('data-bundle') !== name) return;
      g.classList.add('nucery-cart-page__item--busy');
      (g.getAttribute('data-bundle-keys') || '').split(',').forEach(function (k) { if (k) updates[k] = 0; });
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    return fetch(cartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) { if (cart && Array.isArray(cart.items)) applyCart(cart); return cart; });
  }

  function renderItems(cart) {
    var host = qs('[data-ncp-items]');
    if (!host) return;
    if (!cart.items || !cart.items.length) {
      // Cart emptied — reload to show the server-rendered empty state.
      window.location.reload();
      return;
    }
    // Group bundle lines (shared _bundle property) into one nested block; a full
    // rebuild keeps grouping simple and correct.
    var units = [], bundleIndex = {};
    cart.items.forEach(function (item) {
      var props = item.properties || {};
      var bundleName = props._bundle || props['_bundle'] || '';
      if (bundleName) {
        var u = bundleIndex[bundleName];
        if (!u) { u = { type: 'bundle', name: bundleName, items: [] }; bundleIndex[bundleName] = u; units.push(u); }
        u.items.push(item);
      } else {
        units.push({ type: 'item', item: item });
      }
    });
    host.innerHTML = '';
    units.forEach(function (u) {
      host.appendChild(u.type === 'bundle' ? createBundleGroup(u.name, u.items) : createItem(u.item));
    });
  }

  /* ---------- discounts ---------- */
  var TAG_ICON =
    '<svg class="nucery-cart-page__discount-tag-ic" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L3 13V3h10l7.59 7.59a2 2 0 0 1 0 2.82Z"></path><circle cx="7.5" cy="7.5" r="1.3"></circle></svg>';

  function cartAppliedCodes(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return [];
    return cart.discount_codes.filter(function (d) {
      return d && d.applicable && d.code;
    });
  }

  function cartHasCode(cart, code) {
    if (!code) return false;
    var c = String(code).toUpperCase();
    return cartAppliedCodes(cart).some(function (d) {
      return String(d.code).toUpperCase() === c;
    });
  }

  function renderedCodes() {
    return qsa('[data-ncp-remove-discount]')
      .map(function (b) {
        return b.getAttribute('data-ncp-remove-discount');
      })
      .filter(Boolean);
  }

  function setBusy(busy) {
    var block = qs('[data-ncp-discount]');
    if (block) block.classList.toggle('nucery-cart-page__discount--busy', !!busy);
    var btn = qs('[data-ncp-discount-apply]');
    if (btn) btn.disabled = !!busy;
  }

  function setState(state) {
    var block = qs('[data-ncp-discount]');
    if (!block) return;
    block.classList.toggle('nucery-cart-page__discount--error', state === 'error');
    block.classList.toggle('nucery-cart-page__discount--success', state === 'success');
  }

  function showError(msg) {
    var el = qs('[data-ncp-discount-error]');
    if (msg) {
      if (el) {
        el.textContent = msg;
        el.hidden = false;
      }
      setState('error');
    } else {
      if (el) {
        el.textContent = '';
        el.hidden = true;
      }
      var block = qs('[data-ncp-discount]');
      if (block) block.classList.remove('nucery-cart-page__discount--error');
    }
  }

  function updateCartDiscount(value) {
    return fetch(cartUrl('cart/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: value }),
    }).then(function (r) {
      return r.json();
    });
  }

  function applyDiscount(code) {
    code = String(code == null ? '' : code).trim();
    if (!code || !ensureRoot()) return Promise.resolve();
    readConfig();
    // Fixed-price bundles can't be combined with discount codes.
    if (qsa('[data-bundle-keys]').length) {
      showError(config.strings.discountBundleError || 'Discount codes can’t be used with a bundle.');
      return Promise.resolve();
    }
    setBusy(true);
    showError('');
    var seen = {};
    var union = [];
    renderedCodes()
      .concat([code])
      .forEach(function (c) {
        var k = String(c).toUpperCase();
        if (!seen[k]) {
          seen[k] = 1;
          union.push(c);
        }
      });
    return updateCartDiscount(union.join(','))
      .then(function (cart) {
        applyCart(cart);
        if (cartHasCode(cart, code)) {
          var input = qs('[data-ncp-discount-input]');
          if (input) input.value = '';
          showError('');
          setState('success');
        } else {
          showError(config.strings.discountError || 'This code isn’t valid for your cart.');
        }
        return cart;
      })
      .catch(function () {
        showError(config.strings.discountError || 'Something went wrong. Please try again.');
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function removeDiscount() {
    if (!ensureRoot()) return Promise.resolve();
    setBusy(true);
    return updateCartDiscount('')
      .then(function (cart) {
        applyCart(cart);
        setState('');
        return cart;
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function renderDiscounts(cart) {
    var tags = qs('[data-ncp-discount-tags]');
    if (!tags) return;
    var codes = cartAppliedCodes(cart);
    if (!codes.length) {
      tags.innerHTML = '';
      tags.hidden = true;
      return;
    }
    tags.innerHTML = codes
      .map(function (d) {
        return (
          '<li class="nucery-cart-page__discount-tag">' +
          TAG_ICON +
          '<span class="nucery-cart-page__discount-tag-code">' +
          escapeHtml(d.code) +
          '</span>' +
          '<button type="button" class="nucery-cart-page__discount-remove" data-ncp-remove-discount="' +
          escapeAttr(d.code) +
          '" aria-label="' +
          escapeAttr((config.strings.remove || 'Remove') + ' ' + d.code) +
          '"><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6 18 18M18 6 6 18"></path></svg></button>' +
          '</li>'
        );
      })
      .join('');
    tags.hidden = false;
    showError('');
  }

  function renderSavings(cart) {
    var savings = Number(cart.total_discount) || 0;
    var row = qs('[data-ncp-savings]');
    var amt = qs('[data-ncp-savings-amount]');
    var orig = qs('[data-ncp-subtotal-original]');
    if (amt) amt.textContent = '−' + formatMoney(savings);
    if (row) row.hidden = !(savings > 0);
    if (orig) {
      if (savings > 0) {
        orig.textContent = formatMoney(cartTotalCents(cart) + savings);
        orig.hidden = false;
      } else {
        orig.textContent = '';
        orig.hidden = true;
      }
    }
  }

  function renderTotals(cart) {
    var sub = qs('[data-ncp-subtotal]');
    var chk = qs('[data-ncp-checkout-price]');
    var count = qs('[data-ncp-count]');
    var total = cartTotalCents(cart);
    if (sub) sub.textContent = formatMoney(total);
    if (chk) chk.textContent = formatMoney(total);
    if (count) count.textContent = String(cart.item_count || 0);
    updateHeaderBadges(cart.item_count || 0);
  }

  function cartHasBundle(cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.some(function (it) {
      return it.properties && (it.properties._bundle || it.properties['_bundle']);
    }));
  }

  function applyCart(cart) {
    if (!ensureRoot()) return cart;
    if (cart.currency) config.currency = cart.currency;
    applyThresholdsFromConfig();
    renderItems(cart);
    if (!cart.items || !cart.items.length) return cart; // reload triggered
    renderProgress(cart);
    renderTotals(cart);
    renderDiscounts(cart);
    renderSavings(cart);
    // A fixed-price bundle can't be combined with discount codes — strip any
    // applied code while a bundle is in the cart, then re-render clean.
    if (cartHasBundle(cart) && cartAppliedCodes(cart).length && !applyCart._stripping) {
      applyCart._stripping = true;
      updateCartDiscount('')
        .then(function (c) {
          applyCart._stripping = false;
          if (c && Array.isArray(c.items)) applyCart(c);
        })
        .catch(function () { applyCart._stripping = false; });
    }
    return cart;
  }

  /* ---------- cart note ---------- */
  var noteTimer;
  function saveNote(value) {
    if (noteTimer) clearTimeout(noteTimer);
    noteTimer = setTimeout(function () {
      fetch(cartUrl('cart/update.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value }),
      }).catch(function () {});
    }, 500);
  }

  /* ---------- events ---------- */
  function bind() {
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('#NuceryCartPage')) return;

      var rmBundle = e.target.closest('[data-ncp-remove-bundle]');
      if (rmBundle) {
        e.preventDefault();
        removeBundle(rmBundle.getAttribute('data-ncp-remove-bundle'));
        return;
      }
      var rm = e.target.closest('[data-ncp-remove-line]');
      if (rm) {
        e.preventDefault();
        changeLine(rm.getAttribute('data-ncp-remove-line'), 0);
        return;
      }
      var rmDisc = e.target.closest('[data-ncp-remove-discount]');
      if (rmDisc) {
        e.preventDefault();
        removeDiscount();
        return;
      }
      var delta = e.target.closest('[data-ncp-qty-delta]');
      if (delta) {
        e.preventDefault();
        var row = delta.closest('.nucery-cart-page__item');
        if (!row) return;
        var key = row.getAttribute('data-line-key');
        var d = parseInt(delta.getAttribute('data-ncp-qty-delta'), 10);
        var valEl = row.querySelector('.nucery-cart-page__qty-val');
        var cur = parseInt(valEl && valEl.textContent, 10) || 1;
        var next = Math.max(0, cur + d);
        if (valEl) valEl.textContent = String(next);
        var unit = parseInt(row.getAttribute('data-unit-price'), 10);
        var priceEl = row.querySelector('.nucery-cart-page__item-price');
        if (priceEl && isFinite(unit)) priceEl.textContent = formatMoney(unit * next);
        if (next === 0) row.classList.add('nucery-cart-page__item--busy');
        queueLineChange(key, next);
      }
    });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('[data-ncp-discount-form]')) return;
      e.preventDefault();
      var input = form.querySelector('[data-ncp-discount-input]');
      applyDiscount(input ? input.value : '');
    });

    document.addEventListener('input', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-ncp-discount-input]')) {
        showError('');
        setState('');
      }
    });

    var note = qs('[data-ncp-note-field]');
    if (note) {
      note.addEventListener('input', function () {
        saveNote(note.value);
      });
    }
    var noteToggle = qs('[data-ncp-note-toggle]');
    var noteWrap = qs('[data-ncp-note-wrap]');
    if (noteToggle && noteWrap) {
      noteToggle.addEventListener('change', function () {
        noteWrap.hidden = !noteToggle.checked;
        if (noteToggle.checked && note) note.focus();
      });
    }
  }

  // Theme editor: when a setting changes Shopify re-renders the section into a
  // fresh DOM node. Re-grab the root, re-read the config (new milestone labels,
  // images, colors, thresholds…) and re-render so edits show without a save.
  // bind() is NOT re-run — its listeners are delegated on document and persist.
  function onSectionLoad(e) {
    var sec = e && e.target;
    if (!sec || typeof sec.querySelector !== 'function') return;
    if (!sec.querySelector('#NuceryCartPage') && sec.id !== 'NuceryCartPage') return;
    if (!ensureRoot()) return;
    readConfig();
    fetchCart().then(function (cart) {
      if (cart && Array.isArray(cart.items)) applyCart(cart);
    });
  }

  function init() {
    if (!ensureRoot()) return;
    readConfig();
    bind();
    // Keep totals/discounts/prices in sync with the live cart (plain-text money,
    // no tooltip) without a flash — server already rendered the correct values.
    fetchCart().then(function (cart) {
      if (cart && Array.isArray(cart.items) && cart.items.length) applyCart(cart);
    });
    document.addEventListener('cart:updated', function () {
      fetchCart().then(function (cart) {
        if (cart && Array.isArray(cart.items)) applyCart(cart);
      });
    });
    document.addEventListener('shopify:section:load', onSectionLoad);
    document.addEventListener('shopify:section:select', onSectionLoad);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
